/**
 * The Flight cargo section's values <-> the neutral air cargo block.
 *
 * Service level on the form is one dropdown (General cargo, Passive
 * temperature control, Pharmaceuticals, ...); the neutral request splits it
 * into `service_level` plus a general-cargo `goods_category`. Pieces are
 * capped at MAX_ROWS rows.
 */
import type { AirCargoRequest } from '../../../apis/lanePlanner';

export const MAX_ROWS = 5;

export type AirServiceLevel =
  | 'GENERAL'
  | 'PASSIVE_TEMPERATURE_CONTROL'
  | 'PHARMACEUTICALS'
  | 'PERISHABLES'
  | 'SPECIAL_CARGO'
  | 'OTHER';
export type AirTempRange = 'COL' | 'CRT' | 'ERT' | 'FRO';
export type AirBatteries = 'NONE' | 'ELI' | 'ELM' | 'COMBINATION';

export interface AirPieceValues {
  quantity?: number | string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  weight?: number | string;
  weightBasis?: 'TOTAL' | 'PER_UNIT';
  stackable?: boolean;
}

export interface AirUldValues {
  uldType?: string;
  quantity?: number | string;
  weight?: number | string;
  weightBasis?: 'TOTAL' | 'PER_UNIT';
  volume?: number | string;
}

export interface AirCargoValues {
  serviceLevel: AirServiceLevel;
  tempRangeCode?: AirTempRange;
  commodityName?: string;
  shipmentKind: 'LOOSE' | 'ULD';
  screened: boolean;
  consolidated: boolean;
  knownShipper: boolean;
  pieces: AirPieceValues[];
  ulds: AirUldValues[];
  hasBatteries?: boolean;
  containsBatteries?: AirBatteries;
  /** Zoomlogi-only packaging catalog selection; not sent to vendors. */
  packagingOption?: string;
}

/** Service levels that carry a temperature range. */
export const TEMP_SERVICE_LEVELS: AirServiceLevel[] = [
  'PASSIVE_TEMPERATURE_CONTROL',
  'PHARMACEUTICALS',
  'PERISHABLES',
];

type Category = NonNullable<AirCargoRequest['goods_category']>;

const NEUTRAL_SERVICE: Record<
  AirServiceLevel,
  { service_level: AirCargoRequest['service_level']; category: Category | null }
> = {
  GENERAL: { service_level: 'general', category: null },
  PASSIVE_TEMPERATURE_CONTROL: {
    service_level: 'passive_temp',
    category: null,
  },
  PHARMACEUTICALS: { service_level: 'general', category: 'pharmaceuticals' },
  PERISHABLES: { service_level: 'general', category: 'perishables' },
  SPECIAL_CARGO: { service_level: 'general', category: 'special_cargo' },
  OTHER: { service_level: 'general', category: 'other' },
};

const serviceLevelOf = (cargo: AirCargoRequest): AirServiceLevel => {
  if (cargo.service_level === 'passive_temp')
    return 'PASSIVE_TEMPERATURE_CONTROL';
  return cargo.goods_category
    ? (cargo.goods_category.toUpperCase() as AirServiceLevel)
    : 'GENERAL';
};

export const defaultAirCargoValues = (): AirCargoValues => ({
  serviceLevel: 'GENERAL',
  shipmentKind: 'LOOSE',
  screened: true,
  consolidated: false,
  knownShipper: true,
  pieces: [{ quantity: 1, weightBasis: 'TOTAL', stackable: true }],
  ulds: [{ quantity: 1, weightBasis: 'PER_UNIT' }],
});

/** Validated section values -> the `air` block of the search request. */
export const buildAirCargo = (values: AirCargoValues): AirCargoRequest => {
  const { service_level, category } = NEUTRAL_SERVICE[values.serviceLevel];
  const loose = values.shipmentKind === 'LOOSE';
  const air: AirCargoRequest = {
    service_level,
    shipment_type: loose ? 'loose' : 'uld',
    goods_category: category,
    temp_range: TEMP_SERVICE_LEVELS.includes(values.serviceLevel)
      ? values.tempRangeCode || null
      : null,
    commodity: values.commodityName || null,
    screened: values.screened !== false,
    consolidated: !loose || !!values.consolidated,
    known_shipper: values.knownShipper !== false,
    lithium_battery: values.hasBatteries
      ? values.containsBatteries || 'ELI'
      : 'NONE',
  };
  if (loose) {
    air.pieces = (values.pieces || []).slice(0, MAX_ROWS).map((piece) => ({
      pieces: Number(piece.quantity),
      length_cm: Number(piece.length),
      width_cm: Number(piece.width),
      height_cm: Number(piece.height),
      gross_weight_kg: Number(piece.weight),
      weight_per_item: piece.weightBasis === 'PER_UNIT',
      stackable: piece.stackable !== false,
    }));
  } else {
    air.ulds = (values.ulds || []).slice(0, MAX_ROWS).map((uld) => {
      const units = Number(uld.quantity);
      const weight = Number(uld.weight);
      return {
        units,
        uld_type: String(uld.uldType),
        // The request takes the TOTAL gross weight of the units of this type.
        gross_weight_kg:
          uld.weightBasis === 'PER_UNIT' ? weight * units : weight,
        volume_m3: Number(uld.volume),
      };
    });
  }
  return air;
};

/** Reverse of `buildAirCargo`: a stored `air` block -> section values. */
export const airCargoValuesFromRequest = (
  air: AirCargoRequest
): AirCargoValues => {
  const defaults = defaultAirCargoValues();
  const pieces = air.pieces ?? [];
  const ulds = air.ulds ?? [];
  const batteries =
    air.lithium_battery && air.lithium_battery !== 'NONE'
      ? (air.lithium_battery as AirBatteries)
      : undefined;
  return {
    ...defaults,
    serviceLevel: serviceLevelOf(air),
    tempRangeCode: (air.temp_range as AirTempRange | null) ?? undefined,
    commodityName: air.commodity ?? undefined,
    shipmentKind: air.shipment_type === 'uld' ? 'ULD' : 'LOOSE',
    screened: air.screened !== false,
    consolidated: !!air.consolidated,
    knownShipper: air.known_shipper !== false,
    pieces: pieces.length
      ? pieces.map((piece) => ({
          quantity: piece.pieces,
          length: piece.length_cm,
          width: piece.width_cm,
          height: piece.height_cm,
          weight: piece.gross_weight_kg,
          weightBasis: piece.weight_per_item ? 'PER_UNIT' : 'TOTAL',
          stackable: piece.stackable !== false,
        }))
      : defaults.pieces,
    ulds: ulds.length
      ? ulds.map((uld) => ({
          uldType: uld.uld_type,
          quantity: uld.units,
          weight: uld.gross_weight_kg,
          weightBasis: 'TOTAL',
          volume: uld.volume_m3,
        }))
      : defaults.ulds,
    hasBatteries: !!batteries,
    containsBatteries: batteries,
  };
};
